// packages/client/src/components/project/miller/MillerColumnBrowser.tsx
import { useState, useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { useFolders } from '@/api/folders';
import FolderColumn from './FolderColumn';
import ContentColumn from './ContentColumn';
import SheetColumn from './SheetColumn';
import RevisionColumn from './RevisionColumn';
import NewSetModal from './NewSetModal';
import UploadModal from '../knowledge/UploadModal';
import type { Folder, DrawingSet, DrawingRevision } from '@estimator/shared';

interface MillerColumnBrowserProps {
  projectId: string;
  onOpenViewer?: (knowledgeItemId: string, revisionId?: string) => void;
}

// Sheet type matching SheetColumn's internal type
interface Sheet {
  knowledgeItemId: string;
  title: string;
  latestRevision: DrawingRevision;
  revisionCount: number;
}

export default function MillerColumnBrowser({ projectId, onOpenViewer }: MillerColumnBrowserProps) {
  // Column 1: root folders
  const { data: rootFoldersResponse, isLoading: rootLoading } = useFolders(projectId, 'root');
  const rootFolders = rootFoldersResponse?.data ?? [];
  const unfiledCount = rootFoldersResponse?.unfiled_count ?? 0;

  const [selectedRootFolder, setSelectedRootFolder] = useState<Folder | null>(null);
  const [isUnfiledSelected, setIsUnfiledSelected] = useState(false);
  const [selectedSubfolder, setSelectedSubfolder] = useState<Folder | null>(null);
  const [selectedSet, setSelectedSet] = useState<DrawingSet | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<Sheet | null>(null);

  const [newSetOpen, setNewSetOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  // Column 2: subfolders (only fetched when Plans or another folder with children is selected)
  const selectedRootHasChildren = (selectedRootFolder?.children_count ?? 0) > 0;
  const { data: subfoldersResponse } = useFolders(
    projectId,
    selectedRootHasChildren ? selectedRootFolder?.id : undefined,
  );
  const subfolders = subfoldersResponse?.data ?? [];

  const handleSelectRootFolder = useCallback((folder: Folder | null) => {
    if (folder === null) {
      // "Unfiled" pseudo-folder selected
      setSelectedRootFolder(null);
      setIsUnfiledSelected(true);
    } else {
      setSelectedRootFolder(folder);
      setIsUnfiledSelected(false);
    }
    setSelectedSubfolder(null);
    setSelectedSet(null);
    setSelectedSheet(null);
  }, []);

  const handleSelectSubfolder = useCallback((folder: Folder | null) => {
    setSelectedSubfolder(folder);
    setSelectedSet(null);
    setSelectedSheet(null);
  }, []);

  const handleSelectSet = useCallback((set: DrawingSet) => {
    setSelectedSet(set);
    setSelectedSheet(null);
  }, []);

  if (rootLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="md" color="primary" />
      </div>
    );
  }

  // The "active" folder for content display is the subfolder if one is selected,
  // otherwise the root folder (only when it has no children).
  const activeContentFolder: Folder | null = selectedRootHasChildren
    ? selectedSubfolder
    : selectedRootFolder;

  const isShopDrawings = activeContentFolder?.folder_type === 'SHOP_DRAWINGS';

  return (
    <div className="flex h-full" style={{ minHeight: 400 }}>
      {/* Column 1: Root folders */}
      <FolderColumn
        folders={rootFolders}
        unfiledCount={unfiledCount}
        selectedFolderId={isUnfiledSelected ? '__unfiled__' : selectedRootFolder?.id ?? null}
        onSelectFolder={handleSelectRootFolder}
      />

      {/* Column 2: Subfolders (when the selected root folder has children) */}
      {selectedRootFolder && selectedRootHasChildren && (
        <FolderColumn
          folders={subfolders}
          unfiledCount={0}
          selectedFolderId={selectedSubfolder?.id ?? null}
          onSelectFolder={handleSelectSubfolder}
          columnLabel={selectedRootFolder.name}
        />
      )}

      {/* Column 2 or 3: Content (files or sets) */}
      {(activeContentFolder || isUnfiledSelected) && (
        <ContentColumn
          projectId={projectId}
          folder={activeContentFolder}
          isUnfiled={isUnfiledSelected}
          selectedSetId={selectedSet?.id ?? null}
          onSelectSet={handleSelectSet}
          onSelectFile={(id) => onOpenViewer?.(id)}
          onNewSet={() => setNewSetOpen(true)}
          onUpload={() => setUploadOpen(true)}
        />
      )}

      {/* Column 3 or 4: Sheets (shop drawings only) */}
      {isShopDrawings && selectedSet && (
        <SheetColumn
          projectId={projectId}
          drawingSet={selectedSet}
          selectedSheetKnowledgeItemId={selectedSheet?.knowledgeItemId ?? null}
          onSelectSheet={setSelectedSheet}
        />
      )}

      {/* Column 4 or 5: Revision details (shop drawings only) */}
      {isShopDrawings && selectedSet && selectedSheet && (
        <RevisionColumn
          revision={selectedSheet.latestRevision}
          onOpenViewer={(knowledgeItemId, revisionId) => onOpenViewer?.(knowledgeItemId, revisionId)}
        />
      )}

      {/* Modals */}
      {activeContentFolder && (
        <>
          <NewSetModal
            isOpen={newSetOpen}
            onClose={() => setNewSetOpen(false)}
            projectId={projectId}
            folderId={activeContentFolder.id}
          />
          <UploadModal
            isOpen={uploadOpen}
            onClose={() => setUploadOpen(false)}
            projectId={projectId}
            folderId={activeContentFolder.id}
          />
        </>
      )}
    </div>
  );
}
